import { memo, useRef, useState } from "react";
import { InfoWindow } from "./InfoWindow";
import { MarkerF as GoogleMapsMarker } from "@react-google-maps/api";
import { useMediaQuery, useTheme } from "@mui/material";
import { LightPoint } from "../../models";
import { useGeoMap } from "../../context/GeoMapContext";
import { colors } from "../../theme/theme";
import { IncidentStatus, isIncidentClosed, isIncidentRequiredApproval } from "../../models/IncidentStatus";
import type { Clusterer } from "@react-google-maps/marker-clusterer";

interface Props {
  lightingPoint: LightPoint;
  onShowFaultReport?: (incidentUuid: string) => void;
  clusterer?: Clusterer;
}

export const Marker = memo(({ lightingPoint, onShowFaultReport, clusterer }: Props) => {
  const [hover, toggleHoverEffect] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const { setSelectedMarker, selectedMarker } = useGeoMap();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (lightingPoint.latitude == null || lightingPoint.longitude == null) {
    return null;
  }

  function handleOnClick() {
    setSelectedMarker(lightingPoint);
  }

  function handleOnMouseOver() {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = undefined;
    }
    toggleHoverEffect(true);
  }

  function handleOnMouseOut() {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
    hoverTimeout.current = setTimeout(() => {
      toggleHoverEffect(false);
    }, 200);
  }

  function handleOnCloseInfowWindow() {
    setSelectedMarker(undefined);
  }

  const baseScale = isMobile ? 5 : 3;
  const hoverScale = isMobile ? 7 : 4;

  return (
    <>
      <GoogleMapsMarker
        clusterer={clusterer}
        position={{ lat: lightingPoint.latitude, lng: lightingPoint.longitude }}
        icon={{
          path: google.maps.SymbolPath.CIRCLE,
          scale: hover ? hoverScale : baseScale,
          fillColor: determineColorFromState(lightingPoint?.incident?.incidentStatus),
          strokeColor: determineColorFromState(lightingPoint?.incident?.incidentStatus),
          fillOpacity: 1,
          strokeOpacity: 1
        }}
        onMouseOver={handleOnMouseOver}
        onMouseOut={handleOnMouseOut}
        onClick={handleOnClick}
      />

      {(hover || selectedMarker?.number === lightingPoint.number) && (
        <InfoWindow
          lightPoint={lightingPoint}
          onClose={handleOnCloseInfowWindow}
          incident={lightingPoint?.incident}
          lat={lightingPoint.latitude}
          lng={lightingPoint.longitude}
          onShowFaultReport={onShowFaultReport}
          onMouseEnter={handleOnMouseOver}
          onMouseLeave={handleOnMouseOut}
        />
      )}
    </>
  );
});

const determineColorFromState = (state?: IncidentStatus): string => {
  if (!state) return colors.status.completed;

  if (isIncidentRequiredApproval(state))
    return colors.status.approvalPending;
  if (isIncidentClosed(state))
    return colors.status.completed;
  return colors.status.inProgress;
};

export default Marker;
